/**
 * MobileNavCard — Settings → Customise Navigation
 *
 * v77.99.99 (iter 121.2) — Driven by the central module registry in
 * /lib/navModules.js so newly added modules become pinnable
 * automatically with zero UI work.
 *
 * Behaviour:
 *   • Dashboard and More are permanent anchors (never pinnable).
 *   • Appearance lives inside Settings and isn't a pinnable module.
 *   • Users can choose any 3 modules from the full eligible list.
 *   • Reordering via the chevron buttons (accessible) OR by dragging
 *     the entire pinned row (HTML5 native drag, no library).
 *   • The same logic powers the ⭐ favourites toggle exposed in the
 *     More menu — pin/unpin from either place stays in sync.
 */
import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard, MoreHorizontal, ChevronUp, ChevronDown,
  Pin, RotateCcw, Plus, Check, GripVertical, Star,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  MOBILE_NAV_MODULES, moduleBySlug, moduleVisibleTo, defaultMobileNavSlots,
} from "@/lib/navModules";

function PreviewStrip({ slots }) {
  const items = [
    { slug: "dashboard", label: "Dashboard", icon: LayoutDashboard, fixed: true },
    ...slots.map((slug) => moduleBySlug(slug) || { slug, label: slug, icon: Pin }),
    { slug: "more", label: "More", icon: MoreHorizontal, fixed: true },
  ];
  return (
    <div
      className="flex items-stretch justify-between gap-1 bg-[#F4F8F9] border border-[#D9E1E3] rounded-md p-2 mt-3"
      data-testid="mobile-nav-preview"
    >
      {items.map((it) => {
        const Icon = it.icon || Pin;
        return (
          <div
            key={it.slug}
            className="flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-sm bg-white border border-[#E1E8EA] text-[#2E3A3F]"
          >
            <Icon className="w-4 h-4" strokeWidth={1.6} />
            <span className="text-[10px] leading-tight text-center px-1">{it.label}</span>
            {it.fixed && (
              <span className="text-[8px] uppercase tracking-wider text-[#6F828A]">Fixed</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function MobileNavCard() {
  const { user, refresh } = useAuth();
  const defaults = useMemo(() => defaultMobileNavSlots(user), [user]);

  const initial = useMemo(
    () => (user?.mobile_nav && user.mobile_nav.length > 0
      ? user.mobile_nav.slice(0, 3)
      : defaults),
    [user?.mobile_nav, defaults],
  );

  const [slots, setSlots] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dragSlug, setDragSlug] = useState(null);
  useEffect(() => { setSlots(initial); }, [initial]);

  // Eligible modules = registry filtered by viewer's visibility.
  const eligible = MOBILE_NAV_MODULES.filter((m) => moduleVisibleTo(m, user));
  const available = eligible.filter((m) => !slots.includes(m.slug));
  const dirty = JSON.stringify(slots) !== JSON.stringify(initial);

  const unpin = (slug) => setSlots((s) => s.filter((x) => x !== slug));
  const pin = (slug) => setSlots((s) => {
    if (s.includes(slug) || s.length >= 3) return s;
    return [...s, slug];
  });
  const move = (slug, dir) => setSlots((s) => {
    const i = s.indexOf(slug);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= s.length) return s;
    const out = [...s];
    [out[i], out[j]] = [out[j], out[i]];
    return out;
  });
  // HTML5 drag-and-drop for desktop. Mobile users get the chevrons.
  const onDragStart = (slug) => (e) => {
    setDragSlug(slug);
    try { e.dataTransfer.setData("text/plain", slug); e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onDragOver = (e) => { if (dragSlug) e.preventDefault(); };
  const onDrop = (overSlug) => (e) => {
    e.preventDefault();
    if (!dragSlug || dragSlug === overSlug) return;
    setSlots((s) => {
      const from = s.indexOf(dragSlug);
      const to = s.indexOf(overSlug);
      if (from < 0 || to < 0) return s;
      const out = [...s];
      const [removed] = out.splice(from, 1);
      out.splice(to, 0, removed);
      return out;
    });
    setDragSlug(null);
  };
  const onDragEnd = () => setDragSlug(null);

  const save = async () => {
    if (slots.length === 0) {
      toast.error("Pick at least one module, or reset to defaults.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/me/mobile-nav", { slots });
      await refresh?.();
      toast.success("Mobile navigation saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    setSaving(true);
    try {
      await api.patch("/me/mobile-nav", { slots: [] });
      await refresh?.();
      setSlots(defaults);
      toast.success("Reset to default navigation");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#D9E1E3] rounded-md p-5" data-testid="mobile-nav-card">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Navigation</div>
        <h2 className="font-heading text-lg font-medium text-[#2E3A3F] mt-1">Customise Navigation</h2>
        <p className="text-sm text-[#6F828A] mt-1 leading-relaxed">
          Choose the three modules that sit between <span className="font-medium text-[#2E3A3F]">Dashboard</span> and{" "}
          <span className="font-medium text-[#2E3A3F]">More</span> on the mobile bottom-nav.
          Dashboard and More are always present. Every other module remains accessible through the More menu.
          {" "}You can also <Star className="inline w-3 h-3 text-amber-500 fill-amber-500 mb-px" /> a favourite
          inside the More menu to pin it instantly.
        </p>
      </div>

      <PreviewStrip slots={slots} />

      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">
          Pinned slots ({slots.length}/3)
        </div>
        {slots.length === 0 ? (
          <div className="text-sm text-[#6F828A] bg-[#F4F8F9] border border-dashed border-[#D9E1E3] rounded-md p-4 text-center" data-testid="mobile-nav-empty">
            No modules pinned. Choose up to three from the list below.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="mobile-nav-pinned">
            {slots.map((slug, i) => {
              const m = moduleBySlug(slug) || { slug, label: slug, icon: Pin };
              const Icon = m.icon || Pin;
              return (
                <li
                  key={slug}
                  draggable
                  onDragStart={onDragStart(slug)}
                  onDragOver={onDragOver}
                  onDrop={onDrop(slug)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-2 bg-[#F8FBFB] border border-[#E1E8EA] rounded-sm px-2 py-2.5 cursor-grab active:cursor-grabbing transition-opacity ${dragSlug === slug ? "opacity-40" : ""}`}
                  data-testid={`mobile-nav-slot-${slug}`}
                >
                  <GripVertical className="w-4 h-4 text-[#6F828A] hidden sm:block" aria-hidden />
                  <Icon className="w-4 h-4 text-[#1F9FA4]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm text-[#2E3A3F] font-medium">{m.label}</span>
                  <button type="button" onClick={() => move(slug, -1)} disabled={i === 0}
                    className="p-1.5 rounded-sm hover:bg-[#EEF3F4] disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Move up" data-testid={`mobile-nav-up-${slug}`}>
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => move(slug, +1)} disabled={i === slots.length - 1}
                    className="p-1.5 rounded-sm hover:bg-[#EEF3F4] disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Move down" data-testid={`mobile-nav-down-${slug}`}>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => unpin(slug)}
                    className="text-xs text-[#A33A3A] hover:underline px-2 py-1"
                    data-testid={`mobile-nav-remove-${slug}`}>
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-2 flex items-baseline justify-between">
          <span>Available modules</span>
          <span className="text-[10px] normal-case tracking-normal text-[#6F828A]">
            {slots.length >= 3 ? "Slots full — remove one to pin another." : `${available.length} available`}
          </span>
        </div>
        {available.length === 0 ? (
          <p className="text-xs text-[#6F828A]">All available modules are pinned.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="mobile-nav-available">
            {available.map((m) => {
              const Icon = m.icon || Pin;
              const full = slots.length >= 3;
              return (
                <button
                  key={m.slug}
                  type="button"
                  onClick={() => pin(m.slug)}
                  disabled={full}
                  className={`flex items-center gap-2 bg-white border rounded-sm px-3 py-2.5 text-sm transition-colors text-left ${
                    full
                      ? "border-[#E1E8EA] text-[#A0AAAD] cursor-not-allowed"
                      : "border-[#D9E1E3] hover:border-[#1F9FA4] hover:bg-[#F2FAFA] text-[#2E3A3F]"
                  }`}
                  data-testid={`mobile-nav-add-${m.slug}`}
                >
                  <Icon className={`w-4 h-4 ${full ? "text-[#A0AAAD]" : "text-[#1F9FA4]"}`} strokeWidth={1.6} />
                  <span className="flex-1 truncate">{m.label}</span>
                  <Plus className="w-3 h-3 text-[#6F828A]" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <button type="button" onClick={resetToDefault} disabled={saving}
          className="text-xs text-[#6F828A] hover:text-[#2E3A3F] inline-flex items-center gap-1.5 px-1 py-2 self-start"
          data-testid="mobile-nav-reset">
          <RotateCcw className="w-3 h-3" /> Reset to default
        </button>
        <button type="button" onClick={save} disabled={!dirty || saving}
          className="bg-[#1F9FA4] hover:bg-[#197a7e] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-sm h-10 px-5 text-sm font-medium inline-flex items-center gap-2"
          data-testid="mobile-nav-save">
          <Check className="w-4 h-4" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  );
}

export default MobileNavCard;
